import { LinkListItem } from "../components/link-list";
import Discussion, { DiscussionType } from "../core/interfaces/Discussion";
import { DiscussionJsonPage } from "../core/interfaces/JsonPage";
import {
  VanillaDiscussion,
  VanillaDiscussionsPage,
} from "../core/interfaces/VanillaApi";
import ProviderFactory from "../core/ProviderFactory";
import VanillaDiscussionsProvider from "../core/providers/community/vanillaProviders/VanillaDiscussionsProvider";
import {
  CommunityProviderType,
  ProviderConfiguration,
  VanillaProvider,
} from "../core/types/Provider";
import { isMode } from "../core/utilities";

export default abstract class DiscussionModel {
  private static provider: VanillaProvider = ProviderFactory.create(
    CommunityProviderType.Default,
    isMode("development")
      ? ProviderConfiguration.local
      : ProviderConfiguration.vanilla
  );
  private static discussionFromJsonPageCache: DiscussionJsonPage = null;
  private static discussionsCache: Array<Discussion> = null;
  private static discussionsProvider: VanillaDiscussionsProvider =
    ProviderFactory.create(CommunityProviderType.Discussions);

  public static getDiscussionFromJsonPageCached(): Promise<DiscussionJsonPage> {
    return new Promise((resolve, reject) => {
      if (!DiscussionModel.discussionFromJsonPageCache) {
        DiscussionModel.provider
          .getDiscussionFromJsonPage()
          .then((pageMetadata) => {
            if (pageMetadata && pageMetadata.Discussion) {
              DiscussionModel.discussionFromJsonPageCache = pageMetadata;
              resolve(DiscussionModel.discussionFromJsonPageCache);
            } else {
              DiscussionModel.discussionFromJsonPageCache = null;
              reject("It's not a discussion page.");
            }
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        resolve(DiscussionModel.discussionFromJsonPageCache);
      }
    });
  }

  public static getDiscussionCached(discussionId: number): Promise<Discussion> {
    return new Promise((resolve, reject) => {
      const discussionCached = DiscussionModel.discussionsCache
        ? DiscussionModel.discussionsCache.find(
            (discussionCached) => discussionCached.discussionId === discussionId
          )
        : null;
      if (!discussionCached) {
        DiscussionModel.discussionsCache = [];
        DiscussionModel.provider
          .getDiscussion(discussionId)
          .then((vanillaDiscussion) => {
            const discussion: Discussion = {
              discussionId: vanillaDiscussion.discussionID,
              categoryId: vanillaDiscussion.categoryID,
              type: vanillaDiscussion.type,
              name: vanillaDiscussion.name,
              body: vanillaDiscussion.body,
              url: vanillaDiscussion.url,
              insertUser: {
                id: vanillaDiscussion.insertUser.userID,
                name: vanillaDiscussion.insertUser.name,
                photoUrl: vanillaDiscussion.insertUser.photoUrl,
              },
              lastUser: {
                id: vanillaDiscussion.lastUser.userID,
                name: vanillaDiscussion.lastUser.name,
                photoUrl: vanillaDiscussion.lastUser.photoUrl,
              },
              dateLastComment: new Date(vanillaDiscussion.dateLastComment),
              updateUserID: vanillaDiscussion.updateUserID,
              countComments: vanillaDiscussion.countComments,
              countViews: vanillaDiscussion.countViews,
            };
            if (discussion.type === DiscussionType.question) {
              discussion.attributes = {
                status: vanillaDiscussion.attributes.question.status,
              };
            } else if (discussion.type === DiscussionType.idea) {
              discussion.attributes = {
                status: vanillaDiscussion.attributes.idea.status.name,
                state: vanillaDiscussion.attributes.idea.status.state,
              };
            } else if (discussion.type === DiscussionType.discussion) {
              discussion.attributes = {
                status: "Announcement",
              };
            }
            DiscussionModel.discussionsCache.push(discussion);
            resolve(discussion);
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        resolve(discussionCached);
      }
    });
  }

  public static async getLinkList(
    catId: number,
    limit = 5,
    tags: Array<string> = [],
    announcements = false,
    afterDate: Date = null,
    pageNr = -1
  ): Promise<Array<LinkListItem>> {
    const page = await DiscussionModel.getVanillaDiscussionsPage(
      catId,
      limit,
      tags,
      announcements,
      afterDate,
      pageNr
    );

    return DiscussionModel.pageData2LinkList(page.data, tags.length <= 0);
  }

  public static pageData2LinkList(
    pageData: Array<VanillaDiscussion>,
    useCannonicalUrl: boolean
  ): Array<LinkListItem> {
    return pageData.map((discussion: VanillaDiscussion) => {
      return {
        id: discussion.discussionID,
        text: discussion.name,
        href: useCannonicalUrl ? discussion.canonicalUrl : discussion.url,
        date: new Date(discussion.dateInserted).getTime(),
      };
    });
  }

  public static async getVanillaDiscussionsPage(
    catId: number,
    limit = 5,
    tags: Array<string> = [],
    announcements = false,
    afterDate: Date = null,
    pageNr = -1
  ): Promise<VanillaDiscussionsPage> {
    const page = await DiscussionModel.discussionsProvider.getDiscussionsByCat(
      catId,
      limit,
      tags,
      announcements,
      afterDate,
      pageNr
    );

    return page;
  }

  public static async searchDiscussions(
    query: string,
    categoryId: number = null,
    page = 1,
    limit = 10
  ): Promise<Array<Discussion>> {
    return new Promise((resolve, reject) => {
      DiscussionModel.discussionsProvider
        .searchDiscussions(query, categoryId, page, limit)
        .then((vanillaDiscussions) => {
          const discussions = vanillaDiscussions.map((vanillaDiscussion) => {
            return {
              discussionId: vanillaDiscussion.discussionID,
              categoryId: vanillaDiscussion.categoryID,
              type: vanillaDiscussion.type,
              name: vanillaDiscussion.name,
              body: vanillaDiscussion.body,
              url: vanillaDiscussion.url,
            };
          });
          resolve(discussions);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
}
